import * as yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import React, { useMemo, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Radio,
  Dialog,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  FormGroup,
  Typography,
  IconButton,
  RadioGroup,
  DialogTitle,
  DialogContent,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { isValidJSON } from 'src/utils/function';
import { currencyInfoList } from 'src/utils/constant';

import { financeApi } from 'src/api/finance';
import { useAuthContext } from 'src/auth/hooks';
import { useLocales, useTranslate } from 'src/locales';
import { useDomainContext } from 'src/contexts/domain-context';
import { useTraderAccounts } from 'src/layouts/common/account-popover';

import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import Scrollbar from 'src/components/scrollbar';
import { AmountConverter } from 'src/components/amount-converter';

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'background.paper',
    borderRadius: 1,
  },
  '& .MuiInputLabel-root': {
    bgcolor: 'transparent',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center'
  },
  mt: 1,
};

export const currencyToCountryCode = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  CAD: 'ca',
  AUD: 'au',
  AED: 'ae',
  INR: 'in',
};

export const getCurrenciesList = (domainInfo) => {
  if (!domainInfo?.internal_brand?.available_currencies) {
    return [];
  }

  const availableCurrencies = Object.values(domainInfo?.internal_brand?.available_currencies);
  const enabledCurrencies = domainInfo?.internal_brand?.enabled_currencies
    ? JSON.parse(domainInfo?.internal_brand?.enabled_currencies)
    : null;

  if (enabledCurrencies) {
    return availableCurrencies?.filter((currency) => enabledCurrencies.includes(currency.key));
  }

  return availableCurrencies?.filter((currency) => currency.default);
};

const getFileData = (file) => {
  if (!file) return null;

  return {
    preview: file.type.startsWith('image/')
      ? URL.createObjectURL(file)
      : '/assets/icons/files/ic_pdf.svg',
    name: file.name,
  };
};

export function BankingBankDialog({ open, onClose }) {
  const { t } = useTranslate();
  const { currentLang } = useLocales();
  const { user, balance } = useAuthContext();
  const { domainData: domainInfo } = useDomainContext();
  const { accounts } = useTraderAccounts(user?.id);
  const currencies = getCurrenciesList(domainInfo);

  const bankForms = React.useMemo(() => {
    try {
      return domainInfo?.internal_brand?.bank_details
        ? JSON.parse(domainInfo.internal_brand.bank_details)
        : [];
    } catch (error) {
      console.error('Failed to parse bank_details:', error);
      return [];
    }
  }, [domainInfo?.internal_brand?.bank_details]);

  const [selectedForm, setSelectedForm] = React.useState('');

  const currentForm = bankForms.find((form) => form.id === selectedForm);

  const currentFormInfo = useMemo(() => {
    const current = bankForms.find((form) => form.id === selectedForm);

    let formNameInfo = {};
    let formDescriptionInfo = {};

    if (isValidJSON(current?.name)) {
      const parsedForm = JSON.parse(current?.name);
      formNameInfo = { ...parsedForm };
    } else {
      formNameInfo.en = current?.name;
    }

    if (isValidJSON(current?.description)) {
      const parsedForm = JSON.parse(current?.description);
      formDescriptionInfo = { ...parsedForm };
    } else {
      formDescriptionInfo.en = current?.description;
    }

    return {
      ...current,
      nameInfo: formNameInfo,
      descriptionInfo: formDescriptionInfo,
    };
  }, [bankForms, selectedForm]);

  const validationSchema = yup.object({
    amount: yup.string().required('Amount is required'),
    trading_account_id: yup.string().required('Trading account is required'),
    currency: yup.string().required('Currency is required'),
    form_id: yup.string().required('Form is required'),
    ...currentForm?.settings?.reduce((acc, setting) => {
      if (!setting.isOptional && setting.inputType !== 6) {
        acc[setting.id] =
          setting.inputType !== 3
            ? yup.string().required(`${setting.name} is required`)
            : yup
                .array()
                .min(1, `${setting.name} is required`)
                .of(yup.string().required('Item is required'));
      }
      return acc;
    }, {}),
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      amount: '',
      trading_account_id: '',
      currency: currentForm?.currency && currentForm?.currency !== 0 ? currentForm?.currency : '',
      form_id: '',
      document: null,
    },
  });

  useEffect(() => {
    reset({
      amount: '',
      trading_account_id: '',
      currency: currentForm?.currency && currentForm?.currency !== 0 ? currentForm?.currency : '',
      form_id: selectedForm,
      document: null,
    });
  }, [selectedForm, reset, currentForm?.currency]);

  const [uploadedFiles, setUploadedFiles] = React.useState({});

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const apiFormData = new FormData();

      apiFormData.append('amount', data.amount);
      apiFormData.append('trading_account_id', data.trading_account_id);
      apiFormData.append('currency', data.currency);
      apiFormData.append('provider', currentForm?.name);

      const settings = currentForm?.settings
        ?.filter((setting) => setting.inputType !== 5 && setting.inputType !== 6)
        ?.map((setting) => {
          let value;

          if (setting.inputType === 3) {
            value = setting.options
              ?.filter((option) => data[setting.id]?.includes(option.id))
              ?.map((option) => option.option);
          } else if (setting.inputType === 4) {
            value = signatures[setting.id];
          } else if (setting.inputType === 2 && setting.options) {
            const selectedOption = setting.options.find((opt) => opt.id === data[setting.id]);
            value = selectedOption?.option || data[setting.id];
          } else {
            value = data[setting.id];
          }

          return {
            id: setting.id,
            name: setting.name,
            inputType: setting?.inputType,
            isShowCopy: setting?.isShowCopy,
            form_id: currentForm?.id,
            value,
          };
        });

      apiFormData.append('request_data', JSON.stringify(settings));

      currentForm?.settings
        ?.filter((setting) => setting.inputType === 5)
        ?.forEach((setting, index) => {
          const file = uploadedFiles[setting.id];
          if (file) {
            apiFormData.append(`transaction_docs[${index}][name]`, setting.name);
            apiFormData.append(`transaction_docs[${index}][binary]`, file);
          }
        });

      await financeApi.sendBankDeposit(apiFormData);

      enqueueSnackbar(t('Deposit request sent successfully'), { variant: 'success' });

      currentForm?.settings
        ?.filter((setting) => setting.inputType === 5)
        ?.forEach((setting) => {
          const file = uploadedFiles[setting.id];
          if (file?.type?.startsWith('image/')) {
            URL.revokeObjectURL(getFileData(file)?.preview);
          }
        });

      setUploadedFiles({});
      reset();
      setSelectedForm('');
      setShowFormSelector(true);
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [showFormSelector, setShowFormSelector] = React.useState(true);

  const handleFormSelect = (value) => {
    setSelectedForm(value);
    setShowFormSelector(false);
  };

  const [signatures, setSignatures] = React.useState({});
  const sigCanvas = React.useRef({});

  const onClearCanvas = (id) => {
    if (signatures[id]) {
      sigCanvas.current[id].clear();
      setSignatures((prev) => ({ ...prev, [id]: null }));
      setValue(id, '');
    }
  };

  const onSaveCanvas = (id) => {
    setSignatures((prev) => ({ ...prev, [id]: sigCanvas.current[id].toDataURL() }));
    setValue(id, '_');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('Copied to clipboard'), { variant: 'success' });
  };

  useEffect(
    () => () => {
      currentForm?.settings
        ?.filter((setting) => setting.inputType === 5)
        ?.forEach((setting) => {
          const value = watch(setting.id);
          const file = getFileData(value);
          if (file?.preview && !file.preview.includes('ic_pdf.svg')) {
            URL.revokeObjectURL(file.preview);
          }
        });
    },
    [currentForm?.settings, watch]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          px: 3,
          pt: 3,
          typography: 'h4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h4">
          {selectedForm && !showFormSelector
            ? currentFormInfo.nameInfo?.[currentLang.value]?.length > 0
              ? currentFormInfo.nameInfo?.[currentLang.value]
              : (currentFormInfo.nameInfo?.en ?? '')
            : t('Bank Payment')}
        </Typography>

        {selectedForm && !showFormSelector && bankForms?.length > 1 && (
          <Button size="small" onClick={() => setShowFormSelector(true)} sx={{ minWidth: 'auto' }}>
            {t('Change Provider')}
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
          {showFormSelector ? (
            <Controller
              name="form_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  label={t('Select Provider')}
                  error={!!error}
                  helperText={error?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFormSelect(e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                    },
                    mt: 1,
                  }}
                >
                  {bankForms.map((form) => {
                    let formNameInfo = {};

                    if (isValidJSON(form?.name)) {
                      const parsedForm = JSON.parse(form?.name);
                      formNameInfo = { ...parsedForm };
                    } else {
                      formNameInfo.en = form?.name;
                    }
                    return (
                      <MenuItem key={form.id} value={form.id}>
                        {formNameInfo?.[currentLang.value]?.length > 0
                          ? formNameInfo?.[currentLang.value]
                          : (formNameInfo?.en ?? '')}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
            />
          ) : (
            <>
              <Scrollbar sx={{ maxHeight: 680, pl: 0.5, pr: 1.5 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="flex-end">
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          select
                          label={t('Currency')}
                          error={!!error}
                          helperText={error?.message}
                          sx={{ ...textFieldSx, minWidth: 100, flex: '0 0 20%' }}
                          disabled={currentForm?.currency && currentForm?.currency !== 0}
                        >
                          {currentForm?.currency && currentForm?.currency !== 0
                            ? currencyInfoList.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                  <Iconify icon={option.flag} sx={{ mr: 1 }} />
                                  {option.name}
                                </MenuItem>
                              ))
                            : currencies?.map((currency) => (
                                <MenuItem key={currency?.key} value={currency?.key}>
                                  <Iconify
                                    icon={`circle-flags:${currencyToCountryCode[currency?.name]}`}
                                    sx={{ mr: 1 }}
                                  />
                                  {currency?.name}
                                </MenuItem>
                              ))}
                        </TextField>
                      )}
                    />
                    <AmountConverter
                      control={control}
                      setValue={setValue}
                      watch={watch}
                      errors={errors}
                      selectedCurrency={watch('currency')}
                      currencies={currencies}
                      t={t}
                    />
                  </Stack>

                  <Controller
                    name="trading_account_id"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        select
                        label={t('Trading Account')}
                        error={!!error}
                        helperText={error?.message}
                        sx={textFieldSx}
                      >
                        {accounts?.filter(acc => !acc.demo)?.map((acc) => {
                          const parsedBalance = Number(balance?.[acc.id]?.balance ?? 0).toFixed(2).replace(/\.00$/, '');
                          return (
                            <MenuItem key={acc.id} value={acc.id}>
                            {acc.name} {acc?.m_id ? `• ${acc.m_id}` : ""} {balance?.[acc.id]?.balance >=0 ? `• ${currencyInfoList.find(currency => currency.key === acc.currency)?.symbol ?? "$"} ${parsedBalance}` : ""}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    )}
                  />

                  {currentForm?.settings?.map((setting) => {
                    let settingNameInfo = {};
                    if (isValidJSON(setting?.name)) {
                      const parsedForm = JSON.parse(setting?.name);
                      settingNameInfo = { ...parsedForm };
                    } else {
                      settingNameInfo.en = setting?.name;
                    }
                    return (
                      <Stack key={setting.id} spacing={1}>
                        {setting.inputType !== 6 && (
                          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                            {settingNameInfo?.[currentLang.value]?.length > 0
                              ? settingNameInfo?.[currentLang.value]
                              : (settingNameInfo?.en ?? '')}
                          </Typography>
                        )}

                        {setting.inputType === 6 && (
                          <Stack
                            sx={{
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              p: 2,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 2 }}>
                              {settingNameInfo?.[currentLang.value]?.length > 0
                                ? settingNameInfo?.[currentLang.value]
                                : (settingNameInfo?.en ?? '')}
                            </Typography>

                            <Stack spacing={2}>
                              {setting?.options?.map((optionItem) => {
                                let settingOptionInfo = {};

                                if (isValidJSON(optionItem?.option)) {
                                  const parsedForm = JSON.parse(optionItem?.option);
                                  settingOptionInfo = { ...parsedForm };
                                } else {
                                  settingOptionInfo.en = optionItem?.option;
                                }

                                return (
                                  <Stack
                                    key={optionItem.id}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      '&:hover': {
                                        bgcolor: 'background.neutral',
                                      },
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {settingOptionInfo?.[currentLang.value]?.length > 0
                                        ? settingOptionInfo?.[currentLang.value]
                                        : (settingOptionInfo?.en ?? '')}
                                    </Typography>

                                    {setting.isShowCopy && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleCopy(
                                            settingOptionInfo?.[currentLang.value]?.length > 0
                                              ? settingOptionInfo?.[currentLang.value]
                                              : (settingOptionInfo?.en ?? '')
                                          )
                                        }
                                        sx={{
                                          color: 'text.secondary',
                                          '&:hover': {
                                            color: 'primary.main',
                                          },
                                        }}
                                      >
                                        <Iconify icon="solar:copy-linear" width={20} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </Stack>
                        )}

                        {setting.inputType === 1 && (
                          <Controller
                            name={setting.id}
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                fullWidth
                                error={!!error}
                                helperText={error?.message}
                                sx={textFieldSx}
                              />
                            )}
                          />
                        )}

                        {setting.inputType === 2 && (
                          <Controller
                            name={setting.id}
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <>
                                <RadioGroup
                                  value={value}
                                  onChange={onChange}
                                  sx={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                  }}
                                >
                                  {setting.options?.map((optionItem) => {
                                    let settingOptionInfo = {};

                                    if (isValidJSON(optionItem?.option)) {
                                      const parsedForm = JSON.parse(optionItem?.option);
                                      settingOptionInfo = { ...parsedForm };
                                    } else {
                                      settingOptionInfo.en = optionItem?.option;
                                    }

                                    return (
                                      <FormControlLabel
                                        key={optionItem.id}
                                        value={optionItem.id}
                                        control={<Radio />}
                                        label={
                                          settingOptionInfo?.[currentLang.value]?.length > 0
                                            ? settingOptionInfo?.[currentLang.value]
                                            : (settingOptionInfo?.en ?? '')
                                        }
                                        sx={{
                                          bgcolor: 'background.paper',
                                          px: 1,
                                          borderRadius: 1,
                                          mr: 0,
                                        }}
                                      />
                                    );
                                  })}
                                </RadioGroup>
                                {error && <FormHelperText error>{error.message}</FormHelperText>}
                              </>
                            )}
                          />
                        )}

                        {setting.inputType === 3 && (
                          <Controller
                            name={setting.id}
                            control={control}
                            render={({
                              field: { value = [], onChange },
                              fieldState: { error },
                            }) => (
                              <>
                                <FormGroup
                                  sx={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                  }}
                                >
                                  {setting.options?.map((optionItem) => {
                                    let settingOptionInfo = {};

                                    if (isValidJSON(optionItem?.option)) {
                                      const parsedForm = JSON.parse(optionItem?.option);
                                      settingOptionInfo = { ...parsedForm };
                                    } else {
                                      settingOptionInfo.en = optionItem?.option;
                                    }

                                    return (
                                      <FormControlLabel
                                        key={optionItem.id}
                                        control={
                                          <Checkbox
                                            checked={value.includes(optionItem.id)}
                                            onChange={(e) => {
                                              const newValue = e.target.checked
                                                ? [...value, optionItem.id]
                                                : value.filter((v) => v !== optionItem.id);
                                              onChange(newValue);
                                            }}
                                          />
                                        }
                                        label={
                                          settingOptionInfo?.[currentLang.value]?.length > 0
                                            ? settingOptionInfo?.[currentLang.value]
                                            : (settingOptionInfo?.en ?? '')
                                        }
                                        sx={{
                                          bgcolor: 'background.paper',
                                          px: 1,
                                          borderRadius: 1,
                                          mr: 0,
                                        }}
                                      />
                                    );
                                  })}
                                </FormGroup>
                                {error && <FormHelperText error>{error.message}</FormHelperText>}
                              </>
                            )}
                          />
                        )}

                        {setting.inputType === 5 && (
                          <Controller
                            name={setting.id}
                            control={control}
                            render={({ field: { value }, fieldState: { error } }) => {
                              const fileData = getFileData(uploadedFiles[setting.id]);

                              return (
                                <>
                                  {fileData ? (
                                    <Stack
                                      spacing={2}
                                      sx={{
                                        p: 2,
                                        bgcolor: 'background.paper',
                                        borderRadius: 1,
                                        border: error
                                          ? '1px solid #FF4842'
                                          : '1px solid rgba(145, 158, 171, 0.2)',
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" spacing={2}>
                                        {fileData.preview && (
                                          <Stack
                                            component="img"
                                            src={fileData.preview}
                                            sx={{
                                              width: 48,
                                              height: 48,
                                              borderRadius: 1,
                                              objectFit: 'cover',
                                              flexShrink: 0,
                                            }}
                                          />
                                        )}

                                        <Stack spacing={0.5} flexGrow={1}>
                                          <Typography variant="subtitle2">
                                            {fileData.name}
                                          </Typography>
                                        </Stack>

                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setUploadedFiles((prev) => ({
                                              ...prev,
                                              [setting.id]: null,
                                            }));
                                            setValue(setting.id, null);
                                          }}
                                        >
                                          <Iconify icon="eva:close-fill" />
                                        </IconButton>
                                      </Stack>
                                    </Stack>
                                  ) : (
                                    <Upload
                                      file={value}
                                      accept={{ 'image/*': [], 'application/pdf': [] }}
                                      maxSize={3145728}
                                      onDrop={(acceptedFiles) => {
                                        const file = acceptedFiles[0];
                                        if (file) {
                                          setUploadedFiles((prev) => ({
                                            ...prev,
                                            [setting.id]: file,
                                          }));
                                          setValue(setting.id, file.name);
                                        }
                                      }}
                                      sx={{
                                        bgcolor: 'background.paper',
                                        borderRadius: 1,
                                        p: 2,
                                        border: error
                                          ? '1px solid #FF4842'
                                          : '1px solid rgba(145, 158, 171, 0.2)',
                                      }}
                                    />
                                  )}

                                  {error && (
                                    <FormHelperText error sx={{ px: 1 }}>
                                      {error.message}
                                    </FormHelperText>
                                  )}
                                </>
                              );
                            }}
                          />
                        )}

                        {setting.inputType === 4 && (
                          <Controller
                            name={setting.id}
                            control={control}
                            render={({ field: { value }, fieldState: { error } }) => (
                              <>
                                <Stack
                                  sx={{
                                    position: 'relative',
                                    border: error
                                      ? '1px solid #FF4842'
                                      : '1px solid rgba(145, 158, 171, 0.2)',
                                    borderRadius: 1,
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <SignaturePad
                                    ref={(el) => {
                                      sigCanvas.current[setting.id] = el;
                                    }}
                                    onEnd={() => onSaveCanvas(setting.id)}
                                    backgroundColor="transparent"
                                  />
                                  {signatures[setting.id] && (
                                    <IconButton
                                      onClick={() => onClearCanvas(setting.id)}
                                      sx={{ position: 'absolute', right: 8, bottom: 8 }}
                                    >
                                      <Iconify icon="mingcute:eraser-line" width={24} />
                                    </IconButton>
                                  )}
                                </Stack>
                                {error && (
                                  <FormHelperText error sx={{ px: 1 }}>
                                    {error.message}
                                  </FormHelperText>
                                )}
                              </>
                            )}
                          />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Scrollbar>

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{
                  height: 48,
                  borderRadius: 1,
                }}
              >
                {t('Submit')}
              </LoadingButton>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
